if (!customElements.get('infinite-smart-hero')) {
  class InfiniteSmartHero extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-infinite-smart-hero-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-infinite-smart-hero-slide]'));
      this.previousButton = this.querySelector('[data-infinite-smart-hero-previous]');
      this.nextButton = this.querySelector('[data-infinite-smart-hero-next]');
      this.autoplayButton = this.querySelector('[data-infinite-smart-hero-autoplay]');
      this.progress = this.querySelector('[data-infinite-smart-hero-progress]');
      this.progressItemsContainer = this.querySelector('[data-infinite-smart-hero-progress-items]');
      this.progressFill = this.querySelector('[data-infinite-smart-hero-progress-fill]');
      this.counterCurrent = this.querySelector('[data-infinite-smart-hero-counter-current]');
      this.progressItems = [];
      this.status = this.querySelector('[data-infinite-smart-hero-status]');
      this.activeIndex = -1;
      this.animationFrame = null;
      this.lastAnimationTime = null;
      this.elapsedTime = 0;
      this.transitionTimer = null;
      this.pointerInteractionResetTimer = null;
      this.isTransitioning = false;
      this.userPaused = false;
      this.pointerPaused = false;
      this.focusPaused = false;
      this.pointerInteractionActive = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();

      if (!this.stage || this.slides.length === 0) return;

      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} von ${this.slides.length}`);
        slide.inert = true;
      });

      this.bindEvents();

      if (this.isDesignMode) {
        const storedId = this.getStoredValue(this.editorStorageKey);
        const selectedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === storedId);
        this.showImmediately(selectedIndex >= 0 ? selectedIndex : 0);
        return;
      }

      if (this.mode === 'slider') {
        this.buildProgressItems();
        this.showImmediately(0);
        this.preloadAdjacentSlide();
        this.startTimer();
        return;
      }

      this.showImmediately(this.getRandomIndex());
    }

    disconnectedCallback() {
      this.clearTimer();
      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      if (this.pointerInteractionResetTimer) {
        window.clearTimeout(this.pointerInteractionResetTimer);
      }
      this.abortController?.abort();
    }

    get mode() {
      return this.dataset.mode || 'random_session';
    }

    get isDesignMode() {
      return this.dataset.designMode === 'true';
    }

    get storageKey() {
      return `infinite-smart-hero:${this.dataset.sectionId}`;
    }

    get editorStorageKey() {
      return `${this.storageKey}:editor`;
    }

    get canAutoplay() {
      return (
        this.mode === 'slider' &&
        this.dataset.autoplay === 'true' &&
        this.slides.length > 1 &&
        !this.isDesignMode &&
        !this.motionQuery.matches
      );
    }

    get isTemporarilyPaused() {
      return this.pointerPaused || this.focusPaused || document.hidden;
    }

    get pauseOnHover() {
      return this.dataset.pauseOnHover === 'true';
    }

    get indicatorStyle() {
      return this.dataset.indicatorStyle || 'dots';
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => this.goToPrevious(true), { signal });
      this.nextButton?.addEventListener('click', () => this.goToNext(true), { signal });
      this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });

      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
      this.addEventListener('pointerup', () => this.schedulePointerInteractionReset(), { signal });
      this.addEventListener('pointercancel', () => this.schedulePointerInteractionReset(), {
        signal,
      });

      this.addEventListener(
        'mouseenter',
        () => {
          if (!this.pauseOnHover) return;
          this.pointerPaused = true;
          this.clearTimer();
        },
        { signal },
      );
      this.addEventListener(
        'mouseleave',
        () => {
          if (!this.pauseOnHover) return;
          this.pointerPaused = false;
          this.startTimer();
        },
        { signal },
      );
      this.addEventListener(
        'focusin',
        () => {
          if (this.pointerInteractionActive) {
            this.focusPaused = false;
            return;
          }
          this.focusPaused = true;
          this.clearTimer();
        },
        { signal },
      );
      this.addEventListener(
        'focusout',
        () => {
          window.setTimeout(() => {
            this.focusPaused =
              !this.pointerInteractionActive && this.contains(document.activeElement);
            this.startTimer();
          }, 0);
        },
        { signal },
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this.clearTimer();
          else this.startTimer();
        },
        { signal },
      );

      this.motionQuery.addEventListener(
        'change',
        () => {
          this.clearTimer();
          this.startTimer();
        },
        { signal },
      );

      if (this.isDesignMode) {
        document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), {
          signal,
        });
      }
    }

    handlePointerDown(event) {
      if (event.isPrimary === false) return;

      this.pointerInteractionActive = true;
      this.focusPaused = false;
      this.startTimer();
      this.schedulePointerInteractionReset();
    }

    schedulePointerInteractionReset() {
      if (this.pointerInteractionResetTimer) {
        window.clearTimeout(this.pointerInteractionResetTimer);
      }

      this.pointerInteractionResetTimer = window.setTimeout(() => {
        this.pointerInteractionActive = false;
        this.pointerInteractionResetTimer = null;
      }, 350);
    }

    buildProgressItems() {
      if (!this.progressItemsContainer) return;

      const fragment = document.createDocumentFragment();
      this.slides.forEach((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'infinite-smart-hero__progress-item';
        button.dataset.slideIndex = String(index);
        button.setAttribute('aria-label', `Banner ${index + 1} anzeigen`);
        button.appendChild(document.createElement('span'));
        button.addEventListener(
          'click',
          () => {
            const direction = index < this.activeIndex ? 'previous' : 'next';
            this.showSlide(index, direction, true);
          },
          { signal: this.abortController.signal },
        );
        fragment.appendChild(button);
      });
      this.progressItemsContainer.replaceChildren(fragment);
      this.progressItems = Array.from(this.progressItemsContainer.children);
    }

    mountSlide(slide) {
      if (!slide || slide.dataset.mounted === 'true') return;

      const template = slide.querySelector('[data-infinite-smart-hero-template]');
      if (template) {
        slide.insertBefore(template.content.cloneNode(true), template);
        template.remove();
      }
      slide.dataset.mounted = 'true';
    }

    showImmediately(index) {
      const selectedSlide = this.slides[index];
      if (!selectedSlide) return;

      this.clearTimer();
      this.elapsedTime = 0;
      this.mountSlide(selectedSlide);

      this.slides.forEach((slide, slideIndex) => {
        const isSelected = slideIndex === index;
        slide.classList.toggle('is-active', isSelected);
        slide.classList.remove('is-leaving');
        slide.hidden = !isSelected;
        slide.setAttribute('aria-hidden', String(!isSelected));
        slide.inert = !isSelected;
      });

      this.activeIndex = index;
      this.updateActiveState(selectedSlide);
    }

    showSlide(index, direction = 'next', announce = false) {
      if (this.isTransitioning || index === this.activeIndex || !this.slides[index]) return;

      const previousSlide = this.slides[this.activeIndex];
      const nextSlide = this.slides[index];
      this.clearTimer();
      this.elapsedTime = 0;
      this.isTransitioning = true;
      this.dataset.direction = direction;
      this.mountSlide(nextSlide);

      nextSlide.hidden = false;
      nextSlide.classList.remove('is-active', 'is-leaving');
      nextSlide.setAttribute('aria-hidden', 'true');
      nextSlide.inert = true;
      void nextSlide.offsetWidth;

      previousSlide?.classList.remove('is-active');
      previousSlide?.classList.add('is-leaving');
      previousSlide?.setAttribute('aria-hidden', 'true');
      if (previousSlide) previousSlide.inert = true;

      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');
      nextSlide.inert = false;
      this.activeIndex = index;
      this.updateActiveState(nextSlide);

      if (announce && this.status) {
        this.status.textContent = `Banner ${index + 1} von ${this.slides.length}`;
      }

      const transitionDuration = this.motionQuery.matches
        ? 0
        : Number.parseInt(this.dataset.transitionMs, 10) || 0;

      this.transitionTimer = window.setTimeout(() => {
        if (previousSlide) {
          previousSlide.classList.remove('is-leaving');
          previousSlide.hidden = true;
        }
        this.transitionTimer = null;
        this.isTransitioning = false;
        this.preloadAdjacentSlide();
        this.startTimer();
      }, transitionDuration);
    }

    updateActiveState(slide) {
      this.style.setProperty(
        '--infinite-smart-hero-active-desktop-ratio',
        slide.dataset.desktopRatio || '1.7778',
      );
      this.style.setProperty(
        '--infinite-smart-hero-active-mobile-ratio',
        slide.dataset.mobileRatio || slide.dataset.desktopRatio || '1.7778',
      );
      this.classList.toggle(
        'is-mobile-text-below',
        slide.classList.contains('infinite-smart-hero__slide--mobile-text-below'),
      );
      this.dataset.activeBlockId = slide.dataset.blockId || '';

      this.progressItems.forEach((item) => {
        item.setAttribute('aria-current', String(Number(item.dataset.slideIndex) === this.activeIndex));
      });

      if (this.counterCurrent) this.counterCurrent.textContent = String(this.activeIndex + 1);
      this.renderProgress();
    }

    preloadAdjacentSlide() {
      if (this.mode !== 'slider' || this.slides.length < 2) return;
      const nextIndex = (this.activeIndex + 1) % this.slides.length;
      this.mountSlide(this.slides[nextIndex]);
    }

    goToNext(announce = false) {
      if (this.slides.length < 2) return;
      const nextIndex = (this.activeIndex + 1) % this.slides.length;
      this.showSlide(nextIndex, 'next', announce);
    }

    goToPrevious(announce = false) {
      if (this.slides.length < 2) return;
      const previousIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
      this.showSlide(previousIndex, 'previous', announce);
    }

    startTimer() {
      this.clearTimer();
      if (
        !this.isConnected ||
        !this.canAutoplay ||
        this.userPaused ||
        this.isTemporarilyPaused ||
        this.isTransitioning
      )
        return;

      this.animationFrame = window.requestAnimationFrame((timestamp) =>
        this.updateTimer(timestamp),
      );
    }

    clearTimer() {
      if (this.animationFrame !== null) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      this.lastAnimationTime = null;
    }

    updateTimer(timestamp) {
      if (
        !this.isConnected ||
        !this.canAutoplay ||
        this.userPaused ||
        this.isTemporarilyPaused ||
        this.isTransitioning
      ) {
        this.animationFrame = null;
        this.lastAnimationTime = null;
        return;
      }

      if (this.lastAnimationTime === null) this.lastAnimationTime = timestamp;
      const elapsedFrameTime = Math.min(timestamp - this.lastAnimationTime, 250);
      this.lastAnimationTime = timestamp;
      this.elapsedTime += elapsedFrameTime;

      if (this.elapsedTime >= this.getCurrentDuration()) {
        this.elapsedTime = 0;
        this.renderProgress();
        this.goToNext(false);
        return;
      }

      this.renderProgress();
      this.animationFrame = window.requestAnimationFrame((nextTimestamp) =>
        this.updateTimer(nextTimestamp),
      );
    }

    getCurrentDuration() {
      const activeSlide = this.slides[this.activeIndex];
      const customDuration = Number.parseInt(activeSlide?.dataset.durationMs, 10) || 0;
      const defaultDuration = Number.parseInt(this.dataset.intervalMs, 10) || 6000;
      return customDuration || defaultDuration;
    }

    renderProgress() {
      if (!this.progress) return;

      const duration = this.getCurrentDuration();
      const timedProgress = duration > 0 ? Math.min(this.elapsedTime / duration, 1) : 0;
      const staticProgress = this.slides.length > 0 ? (this.activeIndex + 1) / this.slides.length : 0;
      const progressValue = this.canAutoplay ? timedProgress : staticProgress;

      if (this.progressFill) {
        this.progressFill.style.setProperty('--infinite-smart-hero-progress', progressValue);
      }

      if (this.indicatorStyle !== 'segments') return;

      this.progressItems.forEach((item) => {
        const itemIndex = Number(item.dataset.slideIndex);
        let itemProgress = 0;

        if (itemIndex < this.activeIndex) itemProgress = 1;
        else if (itemIndex === this.activeIndex) itemProgress = this.canAutoplay ? timedProgress : 1;

        item.style.setProperty('--infinite-smart-hero-item-progress', itemProgress);
      });
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.autoplayButton?.classList.toggle('is-paused', this.userPaused);
      this.autoplayButton?.setAttribute(
        'aria-label',
        this.userPaused
          ? 'Automatischen Bannerwechsel starten'
          : 'Automatischen Bannerwechsel pausieren',
      );

      if (this.userPaused) this.clearTimer();
      else this.startTimer();
    }

    handleBlockSelect(event) {
      if (!this.isDesignMode) return;

      let selectedSlide = event.target?.closest?.('[data-infinite-smart-hero-slide]');
      if (!selectedSlide && event.detail?.blockId) {
        selectedSlide = this.slides.find((slide) => slide.dataset.blockId === event.detail.blockId);
      }
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const index = this.slides.indexOf(selectedSlide);
      if (index < 0) return;

      this.setStoredValue(this.editorStorageKey, selectedSlide.dataset.blockId);
      this.showImmediately(index);
    }

    getRandomIndex() {
      if (this.slides.length === 1) return 0;

      if (this.mode === 'random_session') {
        const storedId = this.getStoredValue(this.storageKey);
        const storedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === storedId);
        if (storedIndex >= 0) return storedIndex;

        const selectedIndex = Math.floor(Math.random() * this.slides.length);
        this.setStoredValue(this.storageKey, this.slides[selectedIndex].dataset.blockId);
        return selectedIndex;
      }

      const lastId = this.getStoredValue(`${this.storageKey}:last`);
      const availableIndexes = this.slides
        .map((slide, index) => ({ slide, index }))
        .filter(({ slide }) => slide.dataset.blockId !== lastId)
        .map(({ index }) => index);
      const selectedIndex =
        availableIndexes[Math.floor(Math.random() * availableIndexes.length)] ?? 0;
      this.setStoredValue(`${this.storageKey}:last`, this.slides[selectedIndex].dataset.blockId);
      return selectedIndex;
    }

    getStoredValue(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    setStoredValue(key, value) {
      if (!value) return;
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        // Auswahl bleibt auch ohne verfügbaren Sitzungsspeicher funktionsfähig.
      }
    }
  }

  customElements.define('infinite-smart-hero', InfiniteSmartHero);
}
